import { formatter } from "../../shared/ts/formatter";
import AnnuityType from "./annuityType";
import Constants from "./constants";

export default class HelpText {
  public static TextNotYetDefined(): Array<string> {
    return [
      "The help content on this subject is still to be defined."
    ];
  }

  public static ReqmntAfterTaxIncome(): Array<string> {
    return [
      "This is in line with the new <a href='https://www.retirementlivingstandards.org.uk/details' target='_blank'>Pension and Lifetime Saving Association's</a> Retirement Living Standards adjusted for inflation. It's difficult knowing how much you will need after tax in retirement. To help, the Pension and Lifetime Savings Association have created living standards so you can see what you may need.",
      "By answering the three questions in this page, we will give you an estimate, adjusted for inflation, based on their research. You can either use this figure or overwrite it with any other figure you prefer.",
      "Later on, in our calculations we will see if you are able to get what you want. If not you can solve to see how much you will need to reduce this by to avoid risking running out of money.",
    ];
  }

  public static ReqmntFirstFiveYears(): Array<string> {
    return [
      "Many people also like to have a bit more extra income in the first five years of retirement, if this is the case enter the amount here and we will see if this possible.",
    ];
  }

  public static ReqmntInflation(): Array<string> {
    return [
      "£1,000 ten years after retirement won't buy as much as £1,000 at retirement because of inflation.",
      "Would you like some, or all, future payments to increase at 2.5% to try and keep the same amount of spending power in future as you would have at retirement?",
    ];
  }

  public static ReqmntInflationDuration(): Array<string> {
    return [
      "Many retirees spend more money at the beginning of retirement and less in later years when they may be less active.",
      "If you choose no increases after 75 this means you can get a higher starting income with the same size pot. Your income will buy less each year after 75, but you may expect to spend less anyway.",
    ];
  }

  public static AssetsHeader(): Array<string> {
    return [
      "<strong>State Pensions</strong>",
      'Built up with National Insurance contributions or credits. You need 10 years minimum to qualify. You get the full amount for 35 years. Anything in between and you get a proportion of the full amount. Income paid from State Pension age and increases at a minimum of inflation, earning increases and 2.5%, known as the "triple lock" taxed at your income tax rate.',
      "",
      "<strong>Pension pots</strong>",
      "Built up with contributions from you and possibly your employer over time into a pot. This grows as the investments in your pot grow over time. You can take however you want from age 55. You can use your pot to buy an income for life, or take it as and when you want. You get tax relief on any money you pay in, your investment growth is tax free and you can take 25% tax free at retirement. The rest is taxed at your income tax rate.",
      "",
      "<strong>Final salary pensions</strong>",
      "Built by years spent in an employers scheme and usually based on your salary when you leave. You pay a fixed contribution and your employer paying whatever else is needed, which is nice! You can usually start taking your pension with no reduction somewhere between 60 or 65. You can also exchange some for tax free cash. If you take it earlier (after 55) its usually reduced. Usually increases with inflation. You get tax relief on any money you pay in and any lump sum is tax free. The rest is taxed at your income tax rate.",
      "",
      "<strong>Savings pots</strong>",
      "Bank accounts, Individual savings accounts (ISAs) or other investments you have contributed to and have built up with interest or investmen growth over time. You can take these whenever you want. On some savings pots like ISAs any growth in your savings is tax free. When you take your savings there is no tax to pay.",
      "",
      "<strong>Other cash and income</strong>",
      "Other cash may come from downsizing, or selling a property or selling another asset for example. Other income may come from rent on a property or part time work for example. Cash lump sums may or may not be taxed depending where they are from, other income is likely to be taxed at your income tax rate.",
      "",
      "<strong>Putting it together</strong>",
      "That's what we do! We take all of the above, and put it all together to show you how to get what you want each year after tax. We even keep your tax as low as possible when we do it... enjoy!",
    ];
  }

  public static PensionPots(): Array<string> {
    return [
      'These are the pots you have built up either with a previous employer or on your own. You may have also heard them called "defined contribution" or "money purchase" pensions.',
      "Simply enter the most up to date value you have for each one. If you are still paying into any pension pot please add the total monthly amount which goes into your pot. If this is a scheme run by your employer, this will be the total of both yours and your employer's contributions.",
      "If you start to take income from a defined contribution pension pot, the maximum annual amount that can be contributed (inc employers contributions) to your defined contribution pensions while still getting tax relief will reduce from £40,000 to £10,000. This is known as the <a href='https://www.moneyhelper.org.uk/en/pensions-and-retirement/tax-and-pensions/money-purchase-annual-allowance-mpaa' target='_blank'>Money Purchase Annual Allowance (MPAA)</a>.",
      "We use 5% as a default after charges growth rate in the calculations on this page until you make a change to this default in the design page.",
      "Finally, we assume your contributions will rise as your salary increases.",
    ];
  }

  public static PensionPotsEstValAtRetirement(): Array<string> {
    const taxFreeCashLimit = formatter.formatCurrency(Constants.TaxFreeCashLimit);
    return [
      "The Estimated Value at Retirement is calculated by taking the current value and adding future assumed investment returns between now and the target retirement date.",
      "In addition, all future expected monthly contributions are also added and increased by the assumed investment growth between the date these are paid and the target retirement date.",
      `If you chose to take tax free cash a lump sum, the figure shown will be immediate reduced by 25% up to a maximum of ${taxFreeCashLimit} (as this is the current maximum lump sum most people can take).`,
      `You may have a higher limit if you have previously registered for Lifetime Allowance protection, but we have assumed ${taxFreeCashLimit} is taken in our calculations. If you have a higher limit, we would recommend seeking financial advice to ensure you do not lose this protection.`
    ];
  }

  public static statePension(): Array<string> {
    return [
      "Everyone with enough national insurance contributions receives a State Pension from their State Pension age. We will calculate your" +
      " State Pension age automatically.",
      "If your State Pension age is in future we assume you will get the new full rate State Pension in our calculations so you do not have" +
      " to enter anything here.",
      "If you are over State Pension age, simply enter the amount you are currently receiving each year.",
      'We have assumed you will be entitled to a full State Pension based on 35 years of National Insurance contributions. You can check your actual National Insurance contribution record at <a href="https://www.gov.uk/check-national-insurance-record" target="_blank">gov.uk/check-national-insurance-record</a>.',
      "This is an estimate of your full State Pension. Your actual State Pension may differ for a number of reasons and this figure should not be relied on.",
    ];
  }

  public static finalSalary(): Array<string> {
    return [
      'These are pensions from a current or previous employer which provide an income for life. You may also have heard them called "final salary" or "defined benefit" pensions. These can be a bit more complicated.',
      "Enter when you expect to take these. You will also need to enter the starting amount of pension payable each year. Different parts of these pensions may increase each year by different amounts. If you know the starting amount of these different parts, enter each one. If you don't, just enter the total pension amount and a best guess of the overall increases.",
      "You can get a retirement projection, calculated at the date you want to start taking the pension from, from the scheme administrator if you need one.",
      "You may also take cash from these types of pensions, either on top of your pension or in exchange for some of it. You can add any cash you expect to take in the other cash section below, but only add this if you intend to save it and put it towards your retirement plan, rather than spend it or use it to pay off debts.",
    ];
  }

  public static savingsPots(): Array<string> {
    return [
      "If you have any Savings, ISAs or other Investments which you want to put towards your retirement, enter the current value for each one.",
      "If you intend to continue to build these up with more contributions you can add the monthly amount you expect to pay also.",
    ];
  }

  public static otherCash(): Array<string> {
    return [
      "You may expect one or more cash lump sums in future. If you want to save this money and put some (or all) of it towards your retirement income, rather than spend the money or use it to pay off debts, this will be added to your savings pots.",
      "Enter the amount you expect to get and save and when you expect to get this. Common expected lump sums are;",
      "<i>Downsize your main home</i>",
      "If you are planning to downsize your house or release some of the profit you've made on it, then enter the expected amount you will receive after you have sold this and purchased a smaller home, or the amount of profit you expect to take out, and when you expect this.",
      "<i>Equity Release</i>",
      "If you want to extract equity from your main home and don’t want to move or downsize, then Equity Release could be the answer. By borrowing against your home's value in retirement you can continue to live there and extract some of the equity within it. Further details on this are in our Equity Release menu in our Dashboard section.",
      "<i>Selling a second home or an investment</i>",
      "If you are planning to sell a second house or an investment, then enter the expected profit after any taxes and mortgages you will need to pay and when you expect to get this.",
      "<i>Tax free cash from pensions</i>",
      "You may have some tax free cash from your final salary pensions, or want to save and use some (or all) of the tax free cash from your pension pots if you are taking 25% tax free cash at retirement.",
    ];
  }

  public static otherIncome(): Array<string> {
    return [
      "You may also expect some other non pension income after you retire for a few years. This may come from some part time work for a few years after you start to take a retirement income, or rent from a second home as an example.",
      "Enter the amount, the dates you expect this from and to and whether you expect this to increase each year with inflation.",
    ];
  }

  public static incomeAndPotsCharts(): Array<string> {
    return [
      "This chart tells you if by taking income from your pensions and savings pots on top of everything else you have, whether you can get what you want after tax.",
      "If you cannot, it will show expected shortfalls. You can try to fix these using the Take Less and Pay More solve buttons.",
      "Clicking on any year will also show you a chart of the breakdown of all the different parts of your total income in that year.",
    ];
  }

  public static pensionContributions(): Array<string> {
    return [
      "Where you pay contributions over a period which is not monthly, your current CPT total contributions (your own plus your employers) have been converted into a monthly amount for illustration purposes only.",
    ];
  }

  public static growthProjections(): Array<string> {
    return [
      "Where you have previously saved a plan and entered growth rates manually, we have used the previous growth rates you entered.",
      "You can change the risk assumptions if wanted and these will change.",
      "<strong>What if I have no idea about risk?</strong>",
      "If you have no idea about risk we suggest you use the default of medium risk to build an initial plan and look for qualified investment advice to understand a suitable level of risk for your circumstances. You can find help on <a href='https://www.which.co.uk/money/investing/how-investing-works/what-is-investment-risk-ammz70z8tx1s' target='_blank'>understanding more about investment</a>.",
      "<strong>What if I am happy answering this myself?</strong>",
      "If you are happy to answer this without further advice, we make the following default assumptions based on the returns available on current long term UK government bonds and historic additions to these returns which have been achieved on more risky investments such as UK and overseas shares.",
      "Currently these are average returns of 7.5% a year for a higher risk fund, 5.5% for a medium risk fund, 3.5% for a lower risk and 2.5% for no risk (i.e. cash).",
      "We then deduct 0.5% a year for total provider and investment charges as these are in line with what a low cost provider with low cost default low, medium and higher risk investment funds may charge in total charges.",
      "<strong>These are estimates not guarantees</strong>",
      "Please note these are intended as reasonable estimates of future growth projections and are not guaranteed in any way.",
      "The choice of a suitable growth estimate is your own. You can always change these defaults by manually changing the growth rate assumptions.",
      "If these look inconsistent with the risk level, based on these estimates above, (i.e. there is more than a 1% difference) the risk value will update automatically.",
    ];
  }

  public static protectedPot(): Array<string> {
    return [
      "We will put aside some of your pension pots and not use them for your retirement income. Instead they will be left as a target to pass on, if things go as expected.",
      "We will only use them in the test scenarios to see if you can still get what you want when things don't go to plan (i.e. you may need to dip into these if things don't go to plan).",
    ];
  }

  public static guaranteedIncome(annuityType: AnnuityType): Array<string> {
    if (annuityType == AnnuityType.GuaranteedIncome) {
      return [
        "This is an annual guaranteed income which will be paid every month into your pension pot for as long as you live. You can choose to withdraw it to support your income plan, or keep it invested in your pension pot. Either way, no matter what happens it will be paid in, every year, for life.",
        "The amounts shown are just illustrations. The actual amount you would receive could be higher or lower. Actual rates will depend on your age when you apply, plus they will be personalised to you, so other factors such as your health, lifestyle and where you live will determine the final rate.",
      ];
    }

    return [
      "This is an annual guaranteed income added to your other income.",
      "The amounts shown are just illustrations. The actual amount you would receive could be higher or lower. Actual rates will depend on your age when you apply, plus they will be personalised to you, so other factors such as your health, lifestyle and where you live will determine the final rate.",
    ];
  }

  public static everythingYouWant(): Array<string> {
    return [
      "Everything you want during retirement is set out below. These amounts will change if you use the take less solve button.",
      "Also shown is how much in total you are currently paying into your pension pots.",
    ];
  }

  public static howWeCalcTests(): Array<string> {
    return [
      "These are the details of the tests we have carried out to see if your plan will work. You don't need to solve these if they are not a concern. We try to help by showing, where possible, what actions can be taken to try and reduce the risk of these occuring.",
      "<strong>Lower growth test</strong>",
      "We have tested if you pay 1% a year more in charges (and don't get any extra growth) or pay the same charges but get 1% a year less growth, would you be expected to run out of money?",
      "<strong>Live longer test</strong>",
      "We have tested if you live three years longer than expected, would you be expected to run out of money?",
      "<strong>Market crash test</strong>",
      "We have tested if your savings and pension pots both fall 20% just after you retire, would you be expected to run out of money?",
      "<strong>Need care test</strong>",
      "We have tested if you need £40,000 a year for the last two years of your life to pay for some care, would you be expected to run out of money?",
      "In all cases if you have put aside some money in a protected pot, we use this protected pot also in seeing if your plan will work. Under all tests, these are done individually and everything else remains as expected.",
    ];
  }

  public static livePensionPots(): Array<string> {
    return [
      "The ability to bring in 'live' values is intended to help you see all of your pots in one place and also check the updated charts and outcomes based on these values. To provide these values we use a secure FCA registered dashboard tech provider called PensionLab.",
      "The values shown in our dashboard are expected to be within 0-3 days of their actual value (i.e. what you see in your provider's site or app itself). These timings will vary from provider to provider depending on exactly when they update your funds.",
      "Some providers may intermittently have issues in providing up to date data, for instance if they significantly change or upgrade their site or app, or if you have Multi Factor Authority security set up (like a security text message when you log in for example).",
    ];
  }

  public static healthStateRatherNotSay(): Array<string> {
    return [
      "We will use average life expectancy in our modelling, but any insurer will assume you are fit and healthy.",
      "This may mean guaranteed income type insurance products would pay less income than if you were willing to provide them with some information on your health."
    ];
  }

  public static healthStatePerfect(): Array<string> {
    return [
      "An example of this would be anyone who has never smoked, drinks 10 or less alcohol units per week, is fit and active with no health issues at all.",
    ];
  }

  public static healthStateMinor(): Array<string> {
    return [
      "An example of this would be anyone who has never smoked, but drinks alcohol most days of the week and perhaps takes some medication for high blood pressure or raised cholesterol.",
    ];
  }

  public static healthStateModerate(): Array<string> {
    return [
      "An example of this would be anyone who has smoked regularly for over 10 years, drinks alcohol most days of the week and perhaps takes some medication for say high blood pressure or raised cholesterol.",
    ];
  }

  public static healthStateMajor(): Array<string> {
    return [
      "An example of this would be anyone who has serious health issues which require ongoing medication and treatment.",
      "We will use a lower life expectancy in our modelling, but any insurer would need to understand more on the specific details if you wished to purchase a product like a guaranteed income from them."
    ];
  }

  public static todaysMoney(): Array<string> {
    return [
      "Inflation means we are able to buy less with the same money in future as we can now. A loaf of bread in 1971 cost 10p. Now you won't get a slice for that!",
      "No one knows what inflation will be in future, but if we assume it is 2.5% a year on average (yes it has been higher lately, but 2.5% is just above the Government's long term target), then we can show you what your future income may be worth in today's money.",
    ];
  }

  public static finishTaxEfficiency(): Array<string> {
    return [
      'You pay tax when you take money from your pension pots, but not when you take money from your savings.',
      'We assume you always take enough money from your pension pots before touching your savings to use up all your tax free allowance in any year as you can take this money without paying tax. This lowers the total tax you can expect to pay over the course of your plan.'
    ];
  }

  public static planTableEverythingElse(): Array<string> {
    return [
      'This is the total of your State Pension, final salary pensions and other income in that year.',
      'Where everything else provides more than what you want after tax in any year, only the amount needed is shown.',
      'Any additional income above this is assumed to be put back into your savings and used later if needed.'
    ];
  }

  public static planTableTaxToPay(): Array<string> {
    return [
      'This is the estimated tax you will need to pay over the whole year.',
      'See the assumptions tab for more details on how estimated tax is calculated.',
    ];
  }

  public static planTableTotal(): Array<string> {
    return [
      'This is the total of your income from your pensions and savings pots plus everything else less any estimated tax you need to pay.',
      'This should equal what you want after tax each year, if there were no shortfalls shown.',
      'As the monthly payments are all rounded to the nearest pound and then added up, there may be a few pounds difference.'
    ];
  }

  public static taxFreeLumpSumHoldingFund(): Array<string> {
    return [
      'As you expect to take 25% tax free cash at retirement, you will need to have 25% in cash (the Money Fund) at retirement. Now and '
      + 'at any future review up to retirement you can choose whether to put that 25% in the Money Fund.'
    ];
  }
}
